import * as React from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Sparkline } from "@/components/ui/sparkline";
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import type { LineSummary } from "@/features/lines/hooks";
import { formatRelativeTime } from "@/lib/display-labels";
import { useOeeThresholds } from "@/hooks/useOrganizationPolicy";
import { STATUS_PILL, oeeBand, fakeOeeSeries } from "./helpers";

interface LinesTableProps {
  lines: LineSummary[];
  isGestor: boolean;
  onOpenDetail: (id: string) => void;
  onEdit: (id: string) => void;
  onAskDelete: (line: LineSummary) => void;
}

export function LinesTable({
  lines,
  isGestor,
  onOpenDetail,
  onEdit,
  onAskDelete,
}: LinesTableProps): React.ReactElement {
  // Spec 079 — a régua de cor da fábrica, uma leitura para a tabela inteira.
  const thresholds = useOeeThresholds();
  return (
    <Table>
      <TableHead>
        <tr>
          <TableHeaderCell>Linha</TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
          <TableHeaderCell>OEE 24h</TableHeaderCell>
          <TableHeaderCell className="text-right">Máq.</TableHeaderCell>
          <TableHeaderCell>Alarmes</TableHeaderCell>
          <TableHeaderCell>Responsável</TableHeaderCell>
          <TableHeaderCell>Atualizada</TableHeaderCell>
          <TableHeaderCell />
        </tr>
      </TableHead>
      <TableBody>
        {lines.map((l) => {
          const tone = oeeBand(l.availabilityEstimate7d, thresholds);
          const series = fakeOeeSeries(l.availabilityEstimate7d);
          const pill = STATUS_PILL[l.status];
          return (
            <TableRow key={l.id} data-testid={`line-row-${l.id}`}>
              <TableCell>
                <div>
                  <div className="font-medium text-text-primary">{l.name}</div>
                  {l.description ? (
                    <div className="max-w-[28ch] truncate text-[11px] text-text-tertiary">
                      {l.description}
                    </div>
                  ) : null}
                </div>
              </TableCell>
              <TableCell>
                <Pill tone={pill.tone} withDot>
                  {pill.label}
                </Pill>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <span className="font-mono tabular-nums text-text-primary">
                    {l.availabilityEstimate7d === null
                      ? "—"
                      : `${(l.availabilityEstimate7d * 100).toFixed(1)}%`}
                  </span>
                  {series.length > 0 ? <Sparkline data={series} tone={tone} w={80} h={22} /> : null}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">{l.machineCount}</TableCell>
              <TableCell>
                {l.activeAlarms > 0 ? (
                  <Pill tone="danger" withDot>
                    {l.activeAlarms}
                  </Pill>
                ) : (
                  <span className="text-text-tertiary">0</span>
                )}
              </TableCell>
              <TableCell className="text-text-secondary">{l.responsible ?? "—"}</TableCell>
              <TableCell className="font-mono text-[12px] text-text-tertiary">
                {formatRelativeTime(l.updatedAt)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Abrir detalhes ${l.name}`}
                    onClick={() => onOpenDetail(l.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {isGestor && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Editar ${l.name}`}
                        onClick={() => onEdit(l.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Excluir ${l.name}`}
                        onClick={() => onAskDelete(l)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
